(() => {
  const pad = value => String(value).padStart(2, '0');
  const formatters = new Map();
  const boundaries = new Map();
  let stopped = false;
  let statusPending = false;
  const partsInZone = (timestamp, zone) => {
    if (!formatters.has(zone)) formatters.set(zone, new Intl.DateTimeFormat('en-CA', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }));
    return Object.fromEntries(formatters.get(zone).formatToParts(new Date(timestamp))
      .filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  };
  const dayKey = parts => `${parts.year}-${parts.month}-${parts.day}`;
  const midnightAfter = (now, zone) => {
    const today = partsInZone(now, zone);
    const key = dayKey(today);
    const cached = boundaries.get(zone);
    if (cached?.key === key && cached.deadline > now) return cached.deadline;
    const target = Date.UTC(today.year, today.month - 1, today.day + 1);
    let deadline = target;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const wall = partsInZone(deadline, zone);
      const offset = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second) - deadline;
      const next = target - offset;
      if (next === deadline) break;
      deadline = next;
    }
    if (deadline <= now) throw new RangeError('Invalid market midnight');
    boundaries.set(zone, {key, deadline});
    return deadline;
  };
  const deadlineFor = (panel, now) => panel.dataset.mode === 'daily'
    ? midnightAfter(now, panel.dataset.timezone || 'Europe/Stockholm')
    : Date.parse(panel.dataset.deadline);
  const stopPromotion = () => {
    stopped = true;
    document.getElementById('qs-bf-terms-dialog')?.close?.();
    document.querySelectorAll('[data-qs-refund], .qs-bf-cart-copy, .qs-bf-terms-wrap, #qs-bf-terms-dialog, #qs-bf-cart-promotion')
      .forEach(element => element.remove());
  };
  const tick = () => {
    if (stopped) { stopPromotion(); return; }
    const now = Date.now();
    document.querySelectorAll('[data-qs-refund]').forEach(panel => {
      let deadline;
      try { deadline = deadlineFor(panel, now); }
      catch { panel.querySelector('.qs-bf-time')?.setAttribute('hidden', ''); return; }
      const left = Math.ceil((deadline - now) / 1000);
      if (!Number.isFinite(deadline) || left <= 0) { panel.remove(); return; }
      const label = panel.querySelector('[data-qs-deadline-label]');
      if (label) {
        if (panel.dataset.mode === 'daily') label.textContent = panel.dataset.dailyLabel;
        else {
          try {
            const zone = panel.dataset.timezone || 'Europe/Stockholm';
            label.textContent = dayKey(partsInZone(now, zone)) === dayKey(partsInZone(deadline, zone))
              ? panel.dataset.endsToday : panel.dataset.endsIn;
          } catch { label.textContent = panel.dataset.endsIn; }
        }
      }
      const values = {hours: Math.floor(left / 3600), minutes: Math.floor(left % 3600 / 60), seconds: left % 60};
      Object.entries(values).forEach(([key, value]) => {
        const element = panel.querySelector(`[data-qs-${key}]`);
        if (element && element.textContent !== pad(value)) element.textContent = pad(value);
      });
      panel.querySelector('.qs-bf-time')?.removeAttribute('hidden');
    });
  };
  const readState = root => {
    const marker = root.querySelector('[data-qs-promotion-state]');
    if (!marker) return null;
    try { return JSON.parse(marker.textContent); } catch { return null; }
  };
  const checkStatus = async () => {
    if (stopped || statusPending || document.hidden) return;
    const marker = document.querySelector('[data-qs-promotion-state]');
    if (!marker?.dataset.sectionId) return;
    if (readState(document)?.active === false) { stopPromotion(); return; }
    statusPending = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', marker.dataset.sectionId);
      url.searchParams.set('_qs_status', String(Math.floor(Date.now() / 60000)));
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', signal: controller.signal});
      if (!response.ok) return;
      const rendered = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (readState(rendered)?.active === false) stopPromotion();
    } catch {
      // Keep the last confirmed state when a request fails.
    } finally {
      clearTimeout(timeout);
      statusPending = false;
    }
  };
  let queued = false;
  const observer = new MutationObserver(records => {
    if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1)) || queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      if (readState(document)?.active === false) stopPromotion();
      tick();
    });
  });
  document.addEventListener('click', async event => {
    const link = event.target.closest('a[href="#qs-bf-terms"]');
    if (event.target.closest('[data-qs-close-terms]')) { document.getElementById('qs-bf-terms-dialog')?.close(); return; }
    if (!link) return;
    const terms = document.getElementById('qs-bf-terms');
    const dialog = document.getElementById('qs-bf-terms-dialog');
    if (terms && dialog && typeof dialog.showModal === 'function') {
      event.preventDefault();
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer?.isOpen && typeof cartDrawer.close === 'function') await cartDrawer.close();
      if (stopped || !dialog.isConnected || !terms.isConnected) return;
      dialog.querySelector('[data-qs-terms-content]').replaceChildren(terms.querySelector('div').cloneNode(true));
      if (!dialog.open) dialog.showModal();
    } else if (terms) terms.open = true;
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) { tick(); checkStatus(); }
  });
  window.addEventListener('pageshow', () => { tick(); checkStatus(); });
  if (readState(document)?.active === false) stopPromotion();
  tick(); checkStatus();
  setInterval(tick, 1000);
  setInterval(checkStatus, 60000);
  observer.observe(document.body, {childList: true, subtree: true});
})();
